// Availability tab: the stock quantity of physical products or the license key
// counters. Whether the product sells is the store product's own `enabled`,
// rendered above this section by the built-in tab this one absorbed.
import React, { useEffect, useState } from "react"
import Typography from "aldoo-ra/Typography"
import { ArrowPathIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Button from "aldoo-ra/Button"
import { useUrlParam } from "aldoo-ra/URLState"
import Tooltip from "aldoo-ra/Tooltip"
import { Field, TextInput, DateInput, dateOnly } from "../../../AdminShared/ui"
import { Chip, IconButton, Alert, HelperText } from "../shared/shared"
import { isPresale, presaleOf } from "../../../Storefront/utils/presale"
import { GetVendorProfile } from "../../../AdminShared/api"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const PRESALE_INFO =
  "Pre-sale sells a product before it is out. Its card and page carry a Pre-sale badge, and the cart, the delivery step and the order tell the buyer it is delivered by up to 3 days after the release date. " +
  "It ends by itself on the release date: from that day the product sells as any other. An imported product follows its source (a book the publisher lists as coming); a change made here stays until the source's availability changes."

/** "2026-10-09" of a stored date, for the date input. */
const dayOf = (value) => {
  if (!value) return ""
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10)
}

/**
 * Pre-sale: the switch and the release date it runs until. The switch turns
 * on only with a release date ahead; once that date comes, the product is off
 * pre-sale whatever the switch was (the store reads it so, the API too).
 */
function PresaleSection({ product, read, set }) {
  const t = useProductEditorTexts()
  const releaseDay = dayOf(read("releaseDate"))
  const current = { presale: read("presale"), releaseDate: read("releaseDate") }
  const on = isPresale(current)
  const ahead = !!releaseDay && new Date(`${releaseDay}T00:00:00Z`).getTime() > Date.now()
  const ended = !!read("presale") && !!releaseDay && !ahead
  const promise = presaleOf(current)

  return (
    <div className="flex flex-col gap-3 md:w-1/2" data-testid="presale-section">
      <div className="flex items-center gap-2">
        <Typography component="h4" variant="admin_h5" className="my-0">{t`Pre-sale`}</Typography>
        <HelpHint helpKey="vendor.product.setup#presale" />
        <Tooltip text={t(PRESALE_INFO)}>
          <InformationCircleIcon className="w-5 h-5 text-admin_text_secondary cursor-help" data-testid="presale-info" />
        </Tooltip>
      </div>
      <p className="text-sm text-admin_text_secondary my-0">{t`Sell it before it is out; it ends by itself on the release date.`}</p>
      <div className="flex items-center gap-3">
        <div className={ahead ? "" : "opacity-50 pointer-events-none"} data-testid="presale-toggle">
          <ToggleSwitch variant="admin" isOn={on} onChange={(value) => set("presale", !!value && ahead)} />
        </div>
        <span className="text-sm">{on ? t`On` : t`Off`}</span>
      </div>
      <Field label={t`Release date`}>
        <DateInput
          value={releaseDay}
          onChange={(v) => {
            set("releaseDate", v ? `${v}T00:00:00.000Z` : null)
            // a date moved to today or before ends the pre-sale
            if (!v || new Date(`${v}T00:00:00Z`).getTime() <= Date.now()) set("presale", false)
          }}
          data-testid="field-release-date"
        />
        <HelperText>
          {on && promise
            ? t("On pre-sale until %s; delivered by %s", dateOnly(promise.releaseDate), dateOnly(promise.deliverBy))
            : ended
              ? t("Pre-sale ended on %s: the product sells as any other", dateOnly(releaseDay))
              : t`Pre-sale can be turned on with a release date after today`}
        </HelperText>
      </Field>
    </div>
  )
}

/**
 * How many of this product a buyer may order at once: empty follows the
 * company's limit (Settings → Orders and stock), 0 lifts it for this product —
 * and none of it applies until the company turns limits on
 * (commerce/lib/order-limit). The company's setting is read to say what
 * "empty" means here.
 */
function OrderLimitField({ read, set }) {
  const t = useProductEditorTexts()
  const value = read("maxPerOrder")
  const publisher = read("publisher")
  const [company, setCompany] = useState(null)
  useEffect(() => {
    const id = publisher?._id || publisher
    if (!id) return
    let alive = true
    GetVendorProfile(String(id))
      .then((res) => alive && setCompany(res?.org || res?.organization || res || null))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [publisher?._id || publisher])
  const enabled = company?.orderLimitsEnabled === true
  const companyMax = Number(company?.maxQuantityPerOrder) > 0 ? Number(company.maxQuantityPerOrder) : null
  const placeholder = !company ? t`Company setting` : !enabled ? t`Limits are off for the company` : companyMax ? t("Company setting: %s", companyMax) : t`Company setting: no limit`
  return (
    <div className="md:w-1/2" data-testid="order-limit">
      <Field label={<span className="inline-flex items-center gap-1.5">{t`Maximum items per product`}<HelpHint helpKey="vendor.product.setup#order-limit" /></span>}>
        <TextInput
          type="number"
          min={0}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(v) => set("maxPerOrder", v === "" ? null : Math.max(0, parseInt(v) || 0))}
          data-testid="field-max-per-order"
        />
        <HelperText>
          {t`Limits how many items a customer can purchase of this product.`}{" "}
          {company && !enabled
            ? t`Quantity limits are off for the company, so this has no effect until they are turned on (Settings → Orders and stock).`
            : companyMax
              ? t("Leave empty to use the company's limit (%s); 0 means no limit for this product.", companyMax)
              : t`Leave empty to use the company's limit; 0 means no limit for this product.`}
        </HelperText>
      </Field>
    </div>
  )
}

export default function AvailabilityTab({ editor, shouldShowField, contentType, plkData, reloadPlk, storeValues }) {
  const t = useProductEditorTexts()
  const { product, read, set, reload } = editor
  const isLicenseKey = !!contentType?.isLicenseKey
  // a product sold in variants (a size, a color) has no stock of its own: each variant
  // holds its count (Options), and the product's is their sum
  const [, setProductTab] = useUrlParam("ptab", { default: "" })
  const variants = Array.isArray(storeValues?.variants) ? storeValues.variants : []
  const byVariants = (storeValues?.options || []).some((option) => option?.variantAxis) && variants.length > 0
  const sold = variants.filter((variant) => variant?.enabled !== false)
  const variantStock = sold.filter((variant) => typeof variant?.stock === "number").reduce((sum, variant) => sum + Math.max(0, variant.stock), 0)
  const untracked = sold.some((variant) => variant?.stock === null || variant?.stock === undefined)

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Typography component="h4" variant="admin_h4" className="my-0 mb-2 inline-flex items-center gap-1.5">{t`Inventory Management`}<HelpHint helpKey="vendor.product.setup#inventory" /></Typography>
        <p className="text-sm text-admin_text_secondary my-0">{isLicenseKey ? t`Availability is controlled by the number of free license keys` : t`Stock levels for physical products`}</p>
      </div>

      {isLicenseKey ? (
        <div className="flex items-center gap-3">
          <Chip color="success">{t("Free: %s", product?.stock ?? 0)}</Chip>
          <Chip color="warning">{t("Reserved: %s", plkData?.reservedCount ?? 0)}</Chip>
          <Chip>{t("Taken: %s", plkData?.activeCount ?? 0)}</Chip>
          <Chip color="primary">{t("Total: %s", plkData?.total ?? 0)}</Chip>
          <IconButton title={t`Refresh from database`} onClick={() => { reload(); reloadPlk?.() }}>
            <ArrowPathIcon className="w-4 h-4" />
          </IconButton>
        </div>
      ) : byVariants ? (
        // the count is the variants': shown, and edited where they are
        <>
          <div className="flex flex-col gap-2 md:w-1/2" data-testid="stock-by-variants">
            <Field label={t`Stock Quantity`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-semibold tabular-nums text-admin_text" data-testid="variant-stock-total">
                  {variantStock}
                  {untracked && <span className="text-sm font-normal text-admin_text_secondary"> {t`+ not tracked`}</span>}
                </span>
                <Button variant="admin_secondary" onClick={() => setProductTab("options")} data-testid="edit-variant-stock">
                  {t`Edit stock in Options`}
                </Button>
              </div>
              <HelperText>
                {sold.length === 1
                  ? t("The sum of the stock of %s variant: each has its own count, edited in Options.", sold.length)
                  : t("The sum of the stock of %s variants: each has its own count, edited in Options.", sold.length)}
              </HelperText>
            </Field>
          </div>
          {product?.physicalReservedCount > 0 && <div><Chip color="warning">{t("Reserved: %s", product.physicalReservedCount)}</Chip></div>}
          {!untracked && variantStock <= 0 && <Alert severity="warning" className="md:w-1/2">{t`No variant is in stock: the product cannot be purchased`}</Alert>}
          <PresaleSection product={product} read={read} set={set} />
          <OrderLimitField read={read} set={set} />
        </>
      ) : (
        shouldShowField("stock") && (
          <>
            <div className="flex items-start gap-2 md:w-1/2">
              <Field label={<span className="inline-flex items-center gap-1.5">{t`Stock Quantity`}<HelpHint helpKey="vendor.product.setup#stock" /></span>} className="flex-1">
                <TextInput
                  type="number"
                  min={0}
                  value={read("stock") ?? ""}
                  onChange={(v) => set("stock", v === "" ? null : parseInt(v))}
                  data-testid="field-stock"
                />
                <HelperText>{t`Leave empty for unlimited stock`}</HelperText>
              </Field>
              <IconButton title={t`Refresh stock from database`} onClick={reload} className="mt-5">
                <ArrowPathIcon className="w-4 h-4" />
              </IconButton>
            </div>
            {product?.physicalReservedCount > 0 && <div><Chip color="warning">{t("Reserved: %s", product.physicalReservedCount)}</Chip></div>}
            {read("stock") !== null && read("stock") !== undefined && read("stock") <= 0 && (
              <Alert severity="warning" className="md:w-1/2">{t`This product is out of stock and cannot be purchased`}</Alert>
            )}
            <PresaleSection product={product} read={read} set={set} />
          <OrderLimitField read={read} set={set} />
          </>
        )
      )}
    </div>
  )
}
